import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useUser } from '../../context/UserContext';

// ── Helpers ───────────────────────────────────────────────────
const fmtDate = (val) => {
  if (!val) return null;
  const [y, m, d] = val.split('-');
  if (!y || !m || !d) return val;
  return `${d}-${m}-${y}`;
};

const calcAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? `${age} years` : null;
};

// ── UI Primitives ─────────────────────────────────────────────
const InfoCell = ({ label, value }) => (
  <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    <span className="text-sm font-medium text-slate-800 break-words">
      {value || <span className="text-slate-300 italic">—</span>}
    </span>
  </div>
);

// ── Component ─────────────────────────────────────────────────
function E_Profile() {
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useUser();

  useEffect(() => {
    if (!currentUser?.id) {
      navigate('/login');
      return;
    }

    setLoading(true);

    api.get(`/employee/${currentUser.id}`)
      .then((res) => setEmp(res.data))
      .catch((err) => console.error('Failed to fetch employee', err))
      .finally(() => setLoading(false));
  }, [currentUser, navigate]);

  if (loading || !emp) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          <p className="text-sm text-slate-400 tracking-wider uppercase">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 sm:px-8 py-8">
      <div className="w-full space-y-5">

        {/* ── Employee Profile Card ── */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">

          {/* ── Top Banner: Name + Designation only ── */}
          <div className="h-28 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900 flex flex-col justify-center pl-8">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {emp.name || 'Employee'}
            </h1>
            <p className="text-white text-xl font-medium mt-1">
              {[emp.designation, emp.department].filter(Boolean).join(' · ') || 'Designation'}
            </p>
          </div>

          {/* ── Below Content: Profile Picture + Info Grid ── */}
          <div className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex flex-col xl:flex-row gap-6 xl:gap-8 items-start">

              {/* Profile Picture */}
              <div className="flex-shrink-0 flex justify-center xl:justify-start">
                <div className="w-40 h-40 rounded-3xl overflow-hidden border-4 border-slate-100 shadow-md bg-slate-100">
                  {emp.profilePictureUrl ? (
                    <img
                      src={emp.profilePictureUrl}
                      alt={emp.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <svg
                        className="w-14 h-14"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div className="flex-1 w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoCell label="Employee ID"       value={emp.employeeId} />
                  <InfoCell label="Department"        value={emp.department} />
                  <InfoCell label="Date of Joining"   value={fmtDate(emp.dateOfJoining)} />
                  <InfoCell label="Date of Birth"     value={fmtDate(emp.dateOfBirth)} />
                  <InfoCell label="Reporting Manager" value={emp.reportingManager} />
                  <InfoCell label="Location"          value={emp.location} />
                  <InfoCell label="Email ID"          value={emp.email} />
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </div>
  );
}

export default E_Profile;